<script setup lang="ts">
import { ref } from 'vue'
import { useCallStore } from '@/stores/useCallStore'
import { useUserStore } from '@/stores/useUserStore'
import { storeToRefs } from 'pinia'
import SaveButton from '@/components/common/buttons/SaveButton.vue'
import { useToast } from 'vue-toast-notification'
import { formatDateForOdoo } from '@/utils/dateUtils'
import type { CreateCallDto } from '@/api-swagger'
import Modal from '@/components/common/layout/Modal.vue'
import { NoSymbolIcon } from '@heroicons/vue/24/outline'

import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import MicButton from '@/components/common/inputs/MicButton.vue'

const props = defineProps({
  partnerId: {
    type: Number,
    required: true,
  },
})

const $toast = useToast()

const callStore = useCallStore()
const { loading, loadingCreate } = storeToRefs(callStore)

const userStore = useUserStore()
const { userConnected } = storeToRefs(userStore)

const call = ref<CreateCallDto>()

call.value = {
  callDate: formatDateForOdoo(new Date()),
  userId: userConnected.value?.id || 0,
  partnerId: props.partnerId,
  comment: '',
  doNotDisturb: true,
}

const activeModal = ref(false)

const saveCall = async () => {
  try {
    if (userConnected.value && call.value) {
      call.value.userId = userConnected.value.id
    }

    loading.value = true
    if (call.value) {
      await callStore.createCall(call.value)
      $toast.success('Les modifications ont été enregistrées avec succès.')
      call.value.comment = ''
      activeModal.value = false
      callStore.getCallsByPartnerId(props.partnerId)
    }
  } catch (error) {
    console.error(error)
    $toast.error('Une erreur est survenue lors de la sauvegarde.')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <ButtonDaisy class="text-error" text="" :icon="NoSymbolIcon" @click="activeModal = true" />

  <Modal
    :show-modal="activeModal"
    title="Ne plus contacter"
    :loading="loading"
    @close="activeModal = false"
  >
    <template #content>
      <component :is="NoSymbolIcon" class="text-error mx-auto mt-3 h-8 w-8" />
      <div class="p-4 text-start">
        <div class="p-4 rounded-md mb-4">
          <div class="flex items-center gap-1 mb-2">
            <div class="font-bold">Note de l'appel</div>
            <MicButton :disabled="loading" @result="val => call && (call.comment = call.comment ? call.comment + ' ' + val : val)" />
          </div>
          <textarea
            v-if="call"
            id="call-note"
            class="textarea textarea-bordered w-full h-32 p-2 text-sm"
            v-model="call.comment"
            placeholder="Écrivez les notes de l'appel ici..."
          ></textarea>
          <SaveButton :loading="loadingCreate" @save="saveCall" />
        </div>
      </div>
    </template>
  </Modal>
</template>
